import { useKestrel } from 'features/kestrelViewer'
import {
  ActionsContainer,
  DraggableWrapper,
  DropIndicator,
  LeftSideInfo,
  PrimaryLineContainer,
  useDragHandlers,
} from 'features/kestrelViewer/kestrelViewerMarkup/utils/DragAndDropComponents'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import React, { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Vector2 } from 'three'
import { KVCameraView } from 'types'

import { DeleteCameraViewButton } from './DeleteCameraViewButton'
import { EditCameraViewButton } from './EditCameraViewButton'

const StyledCameraViewEntry = styled.div<{
  $selected: boolean
}>`
  all: unset;
  cursor: ${({ $selected }) => ($selected ? 'auto' : 'pointer')} !important;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.forecast.background4 : ''} !important;
  position: relative;
  padding: 0.25rem;

  &:hover {
    opacity: ${({ $selected }) => ($selected ? 0.8 : 1)} !important;
    background-color: ${({ theme, $selected }) =>
      $selected
        ? theme.forecast.background4
        : theme.forecast.background3} !important;
  }
`

const CameraViewLabel = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.forecast.text1};
  user-select: none;
`

export const CameraViewEntry: FC<{
  cameraView: KVCameraView
  selectedMarkupId: string | undefined
  onReorder?: (
    draggedId: string,
    targetId: string,
    position: 'before' | 'after'
  ) => void
}> = ({ cameraView, selectedMarkupId, onReorder }) => {
  const isSelected = cameraView.id === selectedMarkupId
  const [isHovered, setIsHovered] = useState(false)

  const {
    dropPosition,
    isDragging,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  } = useDragHandlers(cameraView, onReorder || (() => {}), 'cameraviewid')

  const { setPopupMenuStartPosition, setSelectedMarkup, sendKVCommand } =
    useKestrel()

  const entryRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleMouseLeave = (event: MouseEvent) => {
      if (
        entryRef.current &&
        !entryRef.current.contains(event.relatedTarget as Node)
      ) {
        setIsHovered(false)
      }
    }

    const entryElement = entryRef.current
    if (entryElement) {
      entryElement.addEventListener('mouseleave', handleMouseLeave)
    }

    return () => {
      if (entryElement) {
        entryElement.removeEventListener('mouseleave', handleMouseLeave)
      }
    }
  }, [])

  const handleEntryClick = (e: React.MouseEvent) => {
    // Check if the click was on an action button (edit or delete)
    const target = e.target as HTMLElement
    const isActionButton = target.closest('[data-action-button]')

    if (isActionButton) {
      // If clicking on an action button, don't zoom
      return
    }

    // Zoom to camera view when clicking anywhere else on the entry
    setSelectedMarkup(undefined)
    if (sendKVCommand) {
      sendKVCommand({
        command: KestrelCommands.ZOOM_TO_CAMERA_VIEW,
        cameraViewId: cameraView.id,
        cameraViewPosition: cameraView.cameraPosition,
        cameraViewRotation: cameraView.cameraRotation,
      })
    }
  }

  const handleEntryRightClick = () => {
    // Don't handle right clicks when dragging
    if (isDragging) return

    // Right click still opens the edit menu
    if (setPopupMenuStartPosition) {
      setPopupMenuStartPosition(new Vector2(80, 20))
    }
    const newSelectedMarkup =
      selectedMarkupId === cameraView.id ? undefined : cameraView
    setSelectedMarkup(newSelectedMarkup)
  }

  return (
    <StyledCameraViewEntry
      ref={entryRef}
      $selected={isSelected}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleEntryClick}
      onContextMenu={handleEntryRightClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      {dropPosition && <DropIndicator $position={dropPosition} />}
      <DraggableWrapper
        $isDragging={isDragging}
        draggable={true}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}>
        <PrimaryLineContainer>
          <LeftSideInfo>
            <CameraViewLabel>
              {cameraView.name || 'New Camera View'}
            </CameraViewLabel>
          </LeftSideInfo>
          {isHovered && (
            <ActionsContainer>
              <EditCameraViewButton cameraView={cameraView} />
              <DeleteCameraViewButton cameraViewId={cameraView.id} />
            </ActionsContainer>
          )}
        </PrimaryLineContainer>
      </DraggableWrapper>
    </StyledCameraViewEntry>
  )
}
